import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { CasellaPostaSettings } from "@/components/CasellaPostaSettings";

export const GRAFICI_OPZIONI = [
  ["vendite_mese", "Andamento delle vendite mensile"],
  ["riparazioni_mese", "Andamento delle riparazioni mensile"],
  ["prodotti_top", "Prodotti più venduti"],
  ["utili_prodotti", "Utili dei prodotti"],
  ["ore_lavoro", "Ore di lavoro incassate"],
];

const Card = ({ title, sub, children, testId, onSave, isAdmin }) => (
  <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <h2 className="mb-1 font-heading text-base font-bold">{title}</h2>
    <p className="mb-4 text-xs text-slate-500">{sub}</p>
    {children}
    {isAdmin && <Button onClick={onSave} className="mt-4 bg-orange-600 hover:bg-orange-700" data-testid={`${testId}-save`}>Salva</Button>}
  </section>
);

export const GraficiSettings = ({ f, setF, isAdmin, onSave }) => {
  const scelti = f.grafici_dashboard || [];
  const toggle = (k) => setF((s) => ({ ...s, grafici_dashboard: GRAFICI_OPZIONI.map(([x]) => x).filter((x) => (x === k ? !scelti.includes(k) : scelti.includes(x))) }));
  return (
    <Card title="Grafici in dashboard" sub="Scegli quali grafici mostrare. Li vede solo chi ha il permesso (vedi Account staff)." testId="grafici-settings" onSave={onSave} isAdmin={isAdmin}>
      <div className="space-y-2.5">
        {GRAFICI_OPZIONI.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2.5 text-sm">
            <Checkbox disabled={!isAdmin} checked={scelti.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`grafico-check-${k}`} />{l}
          </label>
        ))}
      </div>
    </Card>
  );
};

export const CommercialistaSettings = ({ f, setF, isAdmin, onSave }) => {
  const [busy, setBusy] = useState(false);
  const prova = async () => {
    setBusy(true);
    try {
      if (!(await onSave())) return;
      await api.post("/impostazioni/invia-commercialista-prova");
      toast.success(`Bilancio del mese scorso inviato a ${f.email_commercialista}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Invio al commercialista" sub="Il 1° di ogni mese alle 8:00 il commercialista riceve un'email con i conti del mese precedente e il bilancio Excel." testId="commercialista-settings" onSave={onSave} isAdmin={isAdmin}>
      <label className="mb-4 flex items-center gap-2.5 text-sm font-medium">
        <Checkbox disabled={!isAdmin} checked={Boolean(f.invio_commercialista)} onCheckedChange={(v) => setF((s) => ({ ...s, invio_commercialista: Boolean(v) }))} data-testid="commercialista-attivo-check" />
        Invia ogni mese il bilancio al commercialista
      </label>
      <Input disabled={!isAdmin} type="email" value={f.email_commercialista || ""} onChange={(e) => setF((s) => ({ ...s, email_commercialista: e.target.value }))} placeholder="email del commercialista" data-testid="commercialista-email-input" />
      <CasellaPostaSettings f={f} setF={setF} isAdmin={isAdmin} />
      {isAdmin && f.invio_commercialista && f.email_commercialista && (
        <Button type="button" variant="outline" onClick={prova} disabled={busy} className="mt-3" data-testid="commercialista-prova-button">{busy ? "Invio..." : "Salva e invia ora una prova"}</Button>
      )}
    </Card>
  );
};

const ListaCategorie = ({ label, items, onChange, isAdmin, testId }) => {
  const [nuova, setNuova] = useState("");
  const add = () => {
    const v = nuova.trim();
    if (v && !items.some((x) => x.trim().toLowerCase() === v.toLowerCase())) onChange([...items, v]);
    setNuova("");
  };
  return (
    <div data-testid={testId}>
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {items.map((c, i) => (
          <span key={i} className="flex items-center gap-1 rounded-full bg-slate-100 py-1 pl-3 pr-1.5 text-xs font-medium text-slate-700">
            <input disabled={!isAdmin} value={c} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} className="w-auto bg-transparent outline-none" size={Math.max(c.length, 3)} data-testid={`${testId}-edit-${i}`} />
            {isAdmin && <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-rose-600" data-testid={`${testId}-remove-${i}`}><X className="h-3 w-3" /></button>}
          </span>
        ))}
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <Input value={nuova} onChange={(e) => setNuova(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Nuova categoria" className="h-9" data-testid={`${testId}-input`} />
          <Button type="button" variant="outline" onClick={add} className="h-9" data-testid={`${testId}-add`}>Aggiungi</Button>
        </div>
      )}
    </div>
  );
};

export const CategorieSettings = ({ f, setF, isAdmin, onSave }) => (
  <Card title="Categorie merceologiche" sub="Clicca su una categoria per rinominarla. Gli articoli già salvati mantengono la categoria che avevano." testId="categorie-settings" onSave={onSave} isAdmin={isAdmin}>
    <div className="space-y-5">
      <ListaCategorie label="Prodotti" items={f.categorie_prodotti || []} onChange={(v) => setF((s) => ({ ...s, categorie_prodotti: v }))} isAdmin={isAdmin} testId="categorie-prodotti" />
      <ListaCategorie label="Ricambi" items={f.categorie_ricambi || []} onChange={(v) => setF((s) => ({ ...s, categorie_ricambi: v }))} isAdmin={isAdmin} testId="categorie-ricambi" />
    </div>
  </Card>
);
